import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { CrisisNotice } from "@/components/CrisisNotice";
import { PublicShell } from "@/components/layout/PublicShell";
import { useCity } from "@/contexts/CityContext";
import { isPsychiatrySpecialty } from "@/lib/crisis-notice";
import { areaLabel, areaSlug as toAreaSlug, buildAreas, type LocalListing } from "@/lib/local-pages";
import {
  areaPath,
  areaSeoMetadata,
  breadcrumbStructuredData,
  listingCollectionStructuredData,
  specialtyAreaHeading,
  specialtyAreaPath,
  specialtyAreaSeoMetadata,
} from "@/lib/local-seo";
import { PATIENT_GUIDE } from "@/lib/patient-guide";
import { usePublicDirectory, type Facility } from "@/lib/public-directory";
import { SEO } from "@/lib/seo";
import { slugifySpecialty } from "@/lib/specialty-slug";
import { isThinSpecialty, specialtyHeading } from "@/lib/structured-data";
import { ListingCard } from "@/components/ListingCard";
import { SearchX } from "lucide-react";
import { ListingSkeletons } from "@/components/ListingCard";
import { Button, ButtonLink, EmptyState } from "@/components/system";

/**
 * /directory/area/:areaSlug            — all specialists in a suburb
 * /directory/:specialtySlug/:areaSlug  — one specialty in a suburb
 * Built from real listings only; thin combinations are noindex.
 */
export default function AreaPage() {
  const { areaSlug = "", specialtySlug } = useParams<{ areaSlug: string; specialtySlug?: string }>();
  const { city } = useCity();
  const directory = usePublicDirectory(city.slug, { type: "specialist", limit: 100 });

  const facilities = directory.data?.items ?? [];
  const listings: LocalListing[] = useMemo(
    () =>
      facilities.map((facility) => ({
        slug: facility.slug,
        name: facility.name,
        specialty: facility.address_json.specialty,
        suburb: facility.address_json.suburb,
        practice: facility.address_json.practice_name,
      })),
    [facilities],
  );
  const area = useMemo(() => buildAreas(listings).find((item) => item.slug === areaSlug), [listings, areaSlug]);
  const specialty = specialtySlug ? area?.specialties.find((item) => item.slug === specialtySlug) : undefined;
  const shown: Facility[] = facilities.filter(
    (facility) =>
      Boolean(facility.address_json.suburb?.trim()) &&
      toAreaSlug(facility.address_json.suburb ?? "") === areaSlug &&
      (!specialtySlug || slugifySpecialty(facility.address_json.specialty) === specialtySlug),
  );

  const found = Boolean(area && (!specialtySlug || specialty));
  const metadata = area ? (specialty ? specialtyAreaSeoMetadata(specialty, area) : areaSeoMetadata(area)) : null;
  const heading = area
    ? specialty
      ? specialtyAreaHeading(specialty.slug, specialty.name, area)
      : `Specialists in ${area.name}`
    : directory.isSuccess
      ? "Area not found"
      : `Specialists in ${titleFromSlug(areaSlug)}`;
  const crumbs = area
    ? [
        { name: "Home", path: "/" },
        { name: "Directory", path: "/directory" },
        ...(specialty
          ? [
              { name: specialtyHeading(specialty.slug, specialty.name, city.name), path: `/directory/${specialty.slug}` },
              { name: area.name, path: specialtyAreaPath(specialty.slug, area.slug) },
            ]
          : [{ name: `Specialists in ${area.name}`, path: areaPath(area.slug) }]),
      ]
    : [];

  return (
    <PublicShell>
      {/* Head tags only once data has resolved (never in loading/error). */}
      {directory.isSuccess && metadata && found ? (
        <SEO
          title={metadata.title}
          description={metadata.description}
          path={metadata.path}
          noindex={metadata.noindex}
          follow
          jsonLd={[
            listingCollectionStructuredData(
              metadata,
              heading,
              shown.map((facility) => ({ slug: facility.slug, name: facility.name, specialty: null, suburb: null })),
            ),
            breadcrumbStructuredData(crumbs),
          ]}
        />
      ) : directory.isSuccess ? (
        <SEO
          title="Area not found · PulseBoard"
          description="No specialist listings for this area yet."
          path={specialtySlug ? specialtyAreaPath(specialtySlug, areaSlug) : areaPath(areaSlug)}
          noindex
        />
      ) : null}

      <div className="ds-container">
        <header className="ds-page-head">
          <ol className="ds-crumbs" aria-label="Breadcrumb">
            {(crumbs.length ? crumbs.slice(0, -1) : [{ name: "Home", path: "/" }, { name: "Directory", path: "/directory" }]).map((crumb) => (
              <li key={crumb.path}><Link to={crumb.path}>{crumb.name}</Link></li>
            ))}
          </ol>
          <p className="ds-eyebrow ds-eyebrow--teal">{city.name} specialist directory</p>
          <h1 className="ds-display-1">{heading}</h1>
          {area ? (
            <div className="ds-facts">
              <span><b className="ds-num">{specialty?.count ?? area.count}</b> listed in {areaLabel(area)}, {city.name}</span>
            </div>
          ) : null}
        </header>

        {specialtySlug && isPsychiatrySpecialty(specialtySlug) ? <CrisisNotice /> : null}

        {directory.isLoading ? (
          <ListingSkeletons />
        ) : directory.isError ? (
          <EmptyState
            tone="error"
            icon={<SearchX />}
            title="This page didn't load"
            action={<Button variant="secondary" onClick={() => void directory.refetch()}>Try again</Button>}
          >
            We could not load the current listings. Check your connection and try again.
          </EmptyState>
        ) : !found || !area ? (
          <EmptyState icon={<SearchX />} title="No listings for this area yet" action={<ButtonLink to="/directory">Browse the directory</ButtonLink>}>
            Browse all specialties to find a specialist elsewhere in {city.name}.
          </EmptyState>
        ) : (
          <>
            {!specialty && area.specialties.length > 1 ? (
              <nav className="ds-section ds-section--flush" aria-labelledby="area-specialties">
                <h2 id="area-specialties" className="ds-title">Specialties in {area.name}</h2>
                <ul className="ds-link-grid">
                  {area.specialties.map((item) => (
                    <li key={item.slug}>
                      <Link to={isThinSpecialty(item.count) ? `/directory/${item.slug}` : specialtyAreaPath(item.slug, area.slug)}>
                        <span>{item.name}</span>
                        <span className="ds-num">{item.count}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            ) : null}

            <section className="ds-section" aria-label="Listings">
              <div className="ds-grid">
                {shown.map((facility) => <ListingCard key={facility.id} facility={facility} />)}
              </div>
            </section>

            <section className="ds-section">
              <p className="ds-small">
                {specialty ? (
                  <>
                    <Link className="ds-link" to={`/directory/${specialty.slug}`}>All {specialtyHeading(specialty.slug, specialty.name, city.name).toLowerCase()}</Link>
                    {" · "}
                    <Link className="ds-link" to={areaPath(area.slug)}>All specialists in {area.name}</Link>
                    {" · "}
                  </>
                ) : null}
                <Link className="ds-link" to={PATIENT_GUIDE.path}>How referrals, Medicare and costs work</Link>
              </p>
            </section>
          </>
        )}
      </div>
    </PublicShell>
  );
}

function titleFromSlug(slug: string): string {
  return slug
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
